import { useState } from 'react'

import type { Item } from '../../core/domain/models'
import { AnimatedOptionsPanel, InlineRenameInput, ItemNameWithQuantity } from './ListItem'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
} from '../components/Icons'

interface CompletedSectionProps {
  completedItems: Item[]
  effectiveExpandedItemId: string | null
  activeDragId: string | null
  onToggleExpand: (id: string | null) => void
  onToggleComplete: (id: string, isCompleted: boolean, name: string) => void
  onDelete: (id: string, name: string) => void
  onStartRename?: (id: string) => void
  onSubmitRename?: (id: string, newName: string, oldName: string) => void
  renamingItemId?: string | null
}

export function CompletedSection({
  completedItems,
  effectiveExpandedItemId,
  activeDragId,
  onToggleExpand,
  onToggleComplete,
  onDelete,
  onStartRename,
  onSubmitRename,
  renamingItemId,
}: CompletedSectionProps) {
  const [showCompleted, setShowCompleted] = useState(false)

  if (completedItems.length === 0) return null

  return (
    <div className="completed-items">
      <h3 className="completed-header-heading">
        <button
          type="button"
          onClick={() => setShowCompleted(!showCompleted)}
          className="completed-header"
          aria-expanded={showCompleted}
          aria-controls="completed-items-panel"
        >
          <span className={`chevron ${showCompleted ? 'expanded' : ''}`} aria-hidden="true">
            <ChevronRightIcon />
          </span>
          <span>Completed ({completedItems.length})</span>
        </button>
      </h3>
      <div id="completed-items-panel" hidden={!showCompleted}>
        {showCompleted && completedItems.map((item) => {
          const isExpanded = effectiveExpandedItemId === item.id
          const currentQuantity = typeof item.quantity === 'number' ? item.quantity : 1
          return (
            <div key={item.id} className="item-container">
              <div className={`item-card completed ${isExpanded ? 'expanded' : ''}`}>
                <div className="item-checkbox">
                  <input
                    type="checkbox"
                    checked={item.isCompleted}
                    onChange={() => onToggleComplete(item.id, item.isCompleted, item.name)}
                  />
                </div>
                <div className="item-name">
                  {renamingItemId === item.id ? (
                    <InlineRenameInput
                      currentName={item.name}
                      onSubmit={(newName) => onSubmitRename?.(item.id, newName, item.name)}
                      onCancel={() => onSubmitRename?.(item.id, item.name, item.name)}
                    />
                  ) : (
                    <ItemNameWithQuantity name={item.name} quantity={currentQuantity} />
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (activeDragId) return
                    onToggleExpand(isExpanded ? null : item.id)
                  }}
                  className="item-expand-btn"
                  disabled={!!activeDragId}
                  title={isExpanded ? "Hide options" : "Show options"}
                  aria-label={isExpanded ? "Hide options" : "Show options"}
                  aria-expanded={isExpanded}
                >
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`} aria-hidden="true">
                    <ChevronDownIcon />
                  </span>
                </button>
              </div>
              <AnimatedOptionsPanel isOpen={isExpanded} onClick={(e) => e.stopPropagation()}>
                {onStartRename && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onStartRename(item.id)
                      onToggleExpand(null)
                    }}
                    className="item-option-btn rename"
                    title="Rename"
                    aria-label="Rename"
                  >
                    <PencilIcon />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(item.id, item.name)
                    onToggleExpand(null)
                  }}
                  className="item-option-btn delete"
                  title="Delete"
                  aria-label="Delete"
                >
                  <TrashIcon />
                </button>
              </AnimatedOptionsPanel>
            </div>
          )
        })}
      </div>
    </div>
  )
}
